import type { ReactNode } from 'react';

import { Card } from '@actual-app/components/card';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type { FinancialCommandTarget } from '#components/product-finance/FinancialCommandDialog';
import { formatProductMoney } from '#components/product-finance/money';
import type { ProductMoney } from '#components/product-finance/money';

export function SectionCard({
  title,
  children,
  tone = 'normal',
}: {
  title: ReactNode;
  children: ReactNode;
  tone?: 'normal' | 'overdue';
}) {
  return (
    <Card
      style={{
        margin: 0,
        borderColor:
          tone === 'overdue' ? theme.warningBorder : theme.cardBorder,
      }}
    >
      <View style={{ padding: 16 }}>
        <h2 style={{ fontSize: 18, margin: '0 0 10px' }}>{title}</h2>
        {children}
      </View>
    </Card>
  );
}

export function EmptyStateCard({ children }: { children: ReactNode }) {
  return (
    <Card style={{ maxWidth: 620, margin: '40px auto 0' }}>
      <View style={{ padding: 24 }}>{children}</View>
    </Card>
  );
}

export function EmptySection({ children }: { children: ReactNode }) {
  return <p style={{ color: theme.pageTextSubdued }}>{children}</p>;
}

export function SummaryAmount({
  label,
  money,
}: {
  label: ReactNode;
  money: ProductMoney;
}) {
  return (
    <View>
      <span style={{ color: theme.pageTextSubdued }}>{label}</span>
      <FinancialText as="strong">{formatProductMoney(money)}</FinancialText>
    </View>
  );
}

export function occurrenceTarget(occurrence: {
  id: string;
  name: string;
  amount: ProductMoney;
  date: string;
}): FinancialCommandTarget {
  return {
    occurrenceKey: occurrence.id,
    name: occurrence.name,
    amount: occurrence.amount,
    date: occurrence.date,
  };
}

export function assertNever(value: never): never {
  throw new Error(`Unexpected financial command value: ${String(value)}`);
}
